import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import API_BASE_URL from '../apiConfig';
import './Emails.css';

/* ── Avatar helpers ──────────────────────────────────────────────── */
const AVATAR_PALETTE = [
  '#DB4437', '#E91E63', '#9C27B0', '#673AB7',
  '#3F51B5', '#1976D2', '#0097A7', '#00897B',
  '#388E3C', '#E64A19', '#795548', '#546E7A',
];

function avatarColor(str) {
  if (!str) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function avatarInitial(fromHeader) {
  if (!fromHeader) return '?';
  const m = fromHeader.match(/^([^<]+)</);
  const name = m ? m[1].trim() : fromHeader;
  return name[0].toUpperCase();
}

function senderName(fromHeader) {
  if (!fromHeader) return '(Unknown)';
  const m = fromHeader.match(/^([^<]+)</);
  if (m) return m[1].trim();
  return fromHeader.replace(/<[^>]+>/, '').trim() || fromHeader;
}

function getHeader(email, name) {
  return email?.payload?.headers?.find(h => h.name === name)?.value ?? '';
}

function formatDate(headers) {
  const raw = headers?.find(h => h.name === 'Date')?.value;
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d)) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/* ── Icons ───────────────────────────────────────────────────────── */
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
  </svg>
);

const DraftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM5.92 19H5v-.92l9.06-9.06.92.92L5.92 19zm15.21-10.58l-1.63 1.63-3.75-3.75 1.63-1.63a1 1 0 0 1 1.41 0l2.34 2.34a1 1 0 0 1 0 1.41z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const EmptyMailIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
      fill="#dadce0"
    />
  </svg>
);

/* ── Sub-components ───────────────────────────────────────────────── */
function StatusBanner({ status }) {
  if (!status) return null;
  return (
    <div className={`status-banner status-banner--${status.type}`}>
      {status.message}
    </div>
  );
}

function PriorityBadge({ priority }) {
  if (!priority) return null;
  return (
    <span className={`priority-badge priority-badge--${priority.toLowerCase()}`}>
      {priority}
    </span>
  );
}

function ResponseTag({ requiresResponse }) {
  if (requiresResponse === true) {
    return <span className="response-tag response-tag--reply">↩ Reply needed</span>;
  }
  if (requiresResponse === false) {
    return <span className="response-tag response-tag--fyi">FYI</span>;
  }
  return null;
}

/* ── AI Decision Card ─────────────────────────────────────────────── */
function AiDecisionCard({ email }) {
  const hasDecision = email.requiresResponse !== undefined;
  if (!hasDecision) return null;

  const needsReply = email.requiresResponse === true;
  const hasInsights = !needsReply && email.insights?.hasInsights && email.insights?.synopsis;

  return (
    <div className="ai-decision-card">
      <div className="ai-decision-row">
        <span className="ai-decision-label">Reply needed:</span>
        <span className={`response-badge response-badge--${needsReply ? 'yes' : 'no'}`}>
          {needsReply ? 'YES' : 'NO'}
        </span>
      </div>

      {email.responseReasoning && (
        <p className="ai-decision-reasoning">{email.responseReasoning}</p>
      )}

      {hasInsights && (
        <p className="ai-decision-synopsis">
          <strong>Insights:</strong> {email.insights.synopsis}
        </p>
      )}

      {email.needsHumanReview && (
        <div className="ai-human-review-banner">
          Human review recommended — AI suggests a longer response is needed.
        </div>
      )}
    </div>
  );
}

/* ── Longer Draft Section ─────────────────────────────────────────── */
function LongerDraftSection({ longerDraft }) {
  if (!longerDraft) return null;
  return (
    <div className="longer-draft-section">
      <div className="longer-draft-section__head">
        <span className="longer-draft-section__title">Extended Draft</span>
        <span className="longer-draft-section__note">
          AI-generated · human review required before sending
        </span>
      </div>
      <textarea
        className="draft-section__textarea"
        value={longerDraft}
        readOnly
        aria-label="Extended draft (read-only)"
      />
    </div>
  );
}

/* ── Tag editor options ───────────────────────────────────────── */
const PRIORITY_OPTIONS = [
  { value: 'High',   label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low',    label: 'Low' },
];

const CATEGORY_OPTIONS = [
  { value: 'Work',        label: 'Work' },
  { value: 'Personal',    label: 'Personal' },
  { value: 'Finance',     label: 'Finance' },
  { value: 'Marketing',   label: 'Marketing' },
  { value: 'Newsletter',  label: 'Newsletter' },
  { value: 'Social',      label: 'Social' },
  { value: 'Travel',      label: 'Travel' },
  { value: 'Spam',        label: 'Spam' },
  { value: 'Other',       label: 'Other' },
];

const RESPONSE_OPTIONS = [
  { value: true,     label: '↩ Reply needed' },
  { value: false,    label: 'FYI' },
  { value: 'ignore', label: 'Ignore' },
];

/* ── Generic TagEditor popover ────────────────────────────────── */
function TagEditor({ children, options, currentValue, onSelect }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 5, left: rect.left });
    }
    setOpen(o => !o);
  };

  return (
    <div className="tag-editor" ref={triggerRef}>
      <div
        className="tag-editor__trigger"
        onClick={handleOpen}
        title="Click to edit"
      >
        {children}
        <span className="tag-editor__caret">▾</span>
      </div>
      {open && createPortal(
        <div
          className="tag-popover"
          role="listbox"
          ref={popoverRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
        >
          {options.map(opt => (
            <div
              key={String(opt.value)}
              className={`tag-popover__item${String(opt.value) === String(currentValue) ? ' tag-popover__item--active' : ''}`}
              role="option"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── Editable tag wrappers ────────────────────────────────────── */
function EditablePriorityBadge({ priority, emailId, onEmailUpdate }) {
  if (!priority) return null;
  return (
    <TagEditor
      options={PRIORITY_OPTIONS}
      currentValue={priority}
      onSelect={(val) => onEmailUpdate(emailId, { priority: val }, 'priority', priority)}
    >
      <span className={`priority-badge priority-badge--${priority.toLowerCase()}`}>{priority}</span>
    </TagEditor>
  );
}

function EditableCategoryChip({ category, emailId, onEmailUpdate }) {
  if (!category) return null;
  return (
    <TagEditor
      options={CATEGORY_OPTIONS}
      currentValue={category}
      onSelect={(val) => onEmailUpdate(emailId, { category: val }, 'category', category)}
    >
      <span className="category-chip">{category}</span>
    </TagEditor>
  );
}

function EditableResponseTag({ requiresResponse, emailId, onEmailUpdate }) {
  if (requiresResponse === undefined || requiresResponse === null) return null;
  return (
    <TagEditor
      options={RESPONSE_OPTIONS}
      currentValue={requiresResponse}
      onSelect={(val) => onEmailUpdate(emailId, { requiresResponse: val }, 'requiresResponse', requiresResponse)}
    >
      {requiresResponse === true
        ? <span className="response-tag response-tag--reply">↩ Reply needed</span>
        : requiresResponse === 'ignore'
          ? <span className="response-tag response-tag--ignore">Ignore</span>
          : <span className="response-tag response-tag--fyi">FYI</span>
      }
    </TagEditor>
  );
}

function EmailListItem({ email, isActive, isRead, onClick, onEmailUpdate }) {
  const from  = getHeader(email, 'From');
  const subj  = getHeader(email, 'Subject');
  const name  = senderName(from);
  const color = avatarColor(from);
  const init  = avatarInitial(from);
  const date  = formatDate(email?.payload?.headers);

  const classes = [
    'email-item',
    isActive ? 'email-item--active' : '',
    isRead   ? 'email-item--read'   : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className="email-item__avatar" style={{ backgroundColor: color }}>
        {init}
      </div>
      <div className="email-item__body">
        <div className="email-item__row1">
          <span className="email-item__sender">{name}</span>
          <div className="email-item__meta">
            <EditableResponseTag requiresResponse={email.requiresResponse} emailId={email.id} onEmailUpdate={onEmailUpdate} />
            <EditablePriorityBadge priority={email.priority} emailId={email.id} onEmailUpdate={onEmailUpdate} />
            <span className="email-item__date">{date}</span>
          </div>
        </div>
        <div className="email-item__subject">{subj || '(no subject)'}</div>
        <div className="email-item__row3">
          <span className="email-item__snippet">{email.snippet}</span>
          {email.category && (
            <EditableCategoryChip category={email.category} emailId={email.id} onEmailUpdate={onEmailUpdate} />
          )}
        </div>
      </div>
    </div>
  );
}

function EmailDetail({ email, onBack, onEmailUpdate }) {
  const [draft, setDraft]               = useState('');
  const [draftStatus, setDraftStatus]   = useState(null);
  const [waStatus, setWaStatus]         = useState(null);

  // Reset draft when email changes, pre-populate from pipeline draft if available
  useEffect(() => {
    setDraft(email.draft ?? '');
    setDraftStatus(null);
    setWaStatus(null);
  }, [email.id]);

  const from  = getHeader(email, 'From');
  const subj  = getHeader(email, 'Subject');
  const date  = formatDate(email?.payload?.headers);
  const name  = senderName(from);
  const color = avatarColor(from);
  const init  = avatarInitial(from);

  const handleDraftResponse = useCallback(() => {
    setDraft('');
    setDraftStatus({ type: 'loading', message: 'AI is drafting a response…' });
    fetch(`${API_BASE_URL}/emails/${email.id}/draft-response`, { credentials: 'include' })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
        return data;
      })
      .then(data => {
        setDraft(data);
        setDraftStatus({ type: 'success', message: 'Draft ready — review and send.' });
        setTimeout(() => setDraftStatus(null), 4000);
      })
      .catch(err => {
        setDraftStatus({ type: 'error', message: `Could not generate draft: ${err.message}` });
      });
  }, [email.id]);

  const handleSendWhatsApp = useCallback(() => {
    setWaStatus({ type: 'loading', message: 'Sending to WhatsApp…' });
    fetch(`${API_BASE_URL}/emails/${email.id}/send-whatsapp`, { credentials: 'include' })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
        return data;
      })
      .then(() => {
        setWaStatus({ type: 'success', message: 'Sent to WhatsApp for approval.' });
        setTimeout(() => setWaStatus(null), 5000);
      })
      .catch(err => {
        setWaStatus({ type: 'error', message: `WhatsApp error: ${err.message}` });
      });
  }, [email.id]);

  const hasDraftSection = email.requiresResponse !== false && email.requiresResponse !== 'ignore';

  return (
    <div className="email-detail-panel">
      <div className="email-detail-toolbar">
        <button className="icon-btn" onClick={onBack} title="Back to inbox">
          <BackIcon />
        </button>
      </div>

      <div className="email-detail-content">
        <h1 className="email-detail__subject">{subj || '(no subject)'}</h1>

        {/* From / date row */}
        <div className="email-detail__from-row">
          <div className="email-detail__from-avatar" style={{ backgroundColor: color }}>
            {init}
          </div>
          <div className="email-detail__from-info">
            <div className="email-detail__from-name">{name}</div>
            <div className="email-detail__from-email">{from}</div>
          </div>
          <div className="email-detail__date-label">{date}</div>
        </div>

        {/* AI Summary */}
        {email.summary && (
          <div className="ai-summary-card">
            <div className="ai-summary-card__head">
              <SparkleIcon />
              AI Summary
            </div>
            <p className="ai-summary-card__text">{email.summary}</p>
          </div>
        )}

        {/* Editable tags row */}
        <div className="email-detail__tags-row">
          <EditablePriorityBadge priority={email.priority} emailId={email.id} onEmailUpdate={onEmailUpdate} />
          <EditableCategoryChip category={email.category} emailId={email.id} onEmailUpdate={onEmailUpdate} />
          <EditableResponseTag requiresResponse={email.requiresResponse} emailId={email.id} onEmailUpdate={onEmailUpdate} />
        </div>

        {/* AI Decision Card (reply needed + insights) */}
        <AiDecisionCard email={email} />

        {/* Draft action — show if no pipeline decision yet, or if reply IS needed */}
        {hasDraftSection && (
          <div className="email-detail__actions">
            <StatusBanner status={draftStatus} />
            <button
              className="btn btn--primary"
              onClick={handleDraftResponse}
              disabled={draftStatus?.type === 'loading'}
            >
              <DraftIcon />
              {draftStatus?.type === 'loading'
                ? 'Generating draft…'
                : email.draft
                  ? 'Regenerate Draft'
                  : 'Draft Response with AI'}
            </button>
          </div>
        )}

        {/* Draft editor */}
        {draft && (
          <div className="draft-section">
            <div className="draft-section__head">
              <span className="draft-section__title">Drafted Response</span>
              <span className="draft-section__note">AI-generated · edit before sending</span>
            </div>
            <textarea
              className="draft-section__textarea"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Your response will appear here…"
            />
            <div className="draft-section__footer">
              <StatusBanner status={waStatus} />
              <button
                className="btn btn--whatsapp"
                onClick={handleSendWhatsApp}
                disabled={waStatus?.type === 'loading'}
              >
                <WhatsAppIcon />
                {waStatus?.type === 'loading' ? 'Sending…' : 'Send via WhatsApp'}
              </button>
            </div>
          </div>
        )}

        {/* Extended draft section (when AI says concise draft is insufficient) */}
        {email.needsHumanReview && email.longerDraft && (
          <LongerDraftSection longerDraft={email.longerDraft} />
        )}
      </div>
    </div>
  );
}

/* ── Staleness filter ────────────────────────────────────────────── */
const ONE_DAY_MS = 86_400_000;

// An email is "stale" (archivable) when it's older than 1 day and doesn't need a reply.
// Emails that require a response stay visible regardless of age.
function isStale(email) {
  if (email.requiresResponse === true) return false;
  const raw = email?.payload?.headers?.find(h => h.name === 'Date')?.value;
  if (!raw) return false;
  return Date.now() - new Date(raw).getTime() > ONE_DAY_MS;
}

/* ── Main Emails component ───────────────────────────────────────── */
const READ_IDS_KEY = 'ai-emails-read-ids';

function loadReadIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_IDS_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

function isEmailRead(email, readIds) {
  // Read if: opened in our app, OR explicitly NOT unread in Gmail at sync time
  if (readIds.has(email.id)) return true;
  if (email.labelIds !== undefined && !email.labelIds.includes('UNREAD')) return true;
  return false;
}

const Emails = () => {
  const [emails, setEmails]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [readIds, setReadIds]       = useState(loadReadIds);
  const [showAll, setShowAll]       = useState(false);

  const handleEmailUpdate = useCallback((emailId, updates, field, oldValue) => {
    // Optimistic update
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, ...updates } : e));

    fetch(`${API_BASE_URL}/emails/${emailId}/tags`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        field,
        from: oldValue,
        to: Object.values(updates)[0],
      }),
    }).catch(() => {
      // Rollback on error
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, [field]: oldValue } : e));
    });
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/emails`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => {
          const ms = e => {
            const raw = e?.payload?.headers?.find(h => h.name === 'Date')?.value;
            return raw ? new Date(raw).getTime() : 0;
          };
          return ms(b) - ms(a);
        });
        setEmails(list);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSelect = (emailId) => {
    setSelectedId(emailId);
    // Mark as read in local state + persist to localStorage
    setReadIds(prev => {
      if (prev.has(emailId)) return prev;
      const next = new Set(prev);
      next.add(emailId);
      localStorage.setItem(READ_IDS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const selectedEmail = emails.find(e => e.id === selectedId) ?? null;
  const visibleEmails = showAll ? emails : emails.filter(e => !isStale(e));
  const staleCount    = emails.length - visibleEmails.length;

  return (
    <div className="emails-layout">
      {/* ── Email list ── */}
      <div className={`emails-list-panel${selectedEmail ? ' emails-list-panel--narrow' : ''}`}>
        <div className="emails-list-toolbar">
          <span className="emails-list-toolbar__title">
            Inbox
            {!loading && emails.length > 0 && (
              <span className="emails-list-toolbar__count">({visibleEmails.length})</span>
            )}
          </span>
          <button
            className="icon-btn"
            title="Refresh"
            onClick={() => { setLoading(true); setSelectedId(null); window.location.reload(); }}
          >
            <RefreshIcon />
          </button>
        </div>

        <div className="emails-list-scroller">
          {loading ? (
            <div className="emails-state">
              <div className="spinner" />
              <span>Loading emails…</span>
            </div>
          ) : emails.length === 0 ? (
            <div className="emails-state">
              <EmptyMailIcon />
              <span>Your inbox is empty</span>
            </div>
          ) : (
            <>
              {visibleEmails.map(email => (
                <EmailListItem
                  key={email.id}
                  email={email}
                  isActive={selectedId === email.id}
                  isRead={isEmailRead(email, readIds)}
                  onClick={() => handleSelect(email.id)}
                  onEmailUpdate={handleEmailUpdate}
                />
              ))}
              {staleCount > 0 && (
                <button
                  className="view-all-btn"
                  onClick={() => setShowAll(v => !v)}
                >
                  {showAll
                    ? 'Show less'
                    : `View ${staleCount} older email${staleCount !== 1 ? 's' : ''}`}
                </button>
              )}
              {visibleEmails.length === 0 && staleCount > 0 && (
                <div className="emails-state" style={{ height: 160 }}>
                  <EmptyMailIcon />
                  <span>No recent emails</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Email detail or placeholder ── */}
      {selectedEmail ? (
        <EmailDetail
          email={selectedEmail}
          onBack={() => setSelectedId(null)}
          onEmailUpdate={handleEmailUpdate}
        />
      ) : !loading && emails.length > 0 ? (
        <div className="email-detail-empty">
          <div className="email-detail-empty__inner">
            <EmptyMailIcon />
            <span>Select a message to read</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Emails;
